<template>
    <CardBase
        ref="card" 
        class="card-import" 
        :class="{ 'highlight': isHighlight }"
        :app="app"
        :card="card" 
        :loading="loading"
        :message="message" 
        :actions="actions" 
        :actionable="isActionable" 
        :guidance="guidance" 
        @action="handleAction"
        @dragover.prevent="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop($event)">
    
        <template #title>
            {{ title }}
		</template>

        <template #content>

            <!-- hidden form required to handle system import dialog -->
            <v-file-input
                multiple 
                hide-details 
                ref="fileInput"
                label="select assets"
                style="display:none;" 
                :accept="formats"
                @change="handleSelect">
            </v-file-input>

            <AtomSwap :isPrimary="!hasAssets">

                <!-- select view -->
                <template #primary>
                    <div class="card-import__import">

                        <!-- drag & drop -->
                        <div 
                            class="card-import__import-field" 
                            :class="{ 'highlight': isHighlight }">
                            {{ optionDropDescription }}
                        </div>

                        <!-- options to import an image -->
                        <div class="card-import__import-options">
                            <OptionIcon 
                                v-for="option in activeOptions"
                                :id="option.id"
                                :index="option.index"
                                :icon="option.icon"
                                :label="option.label"
                                :description="option.description(subject)" 
                                @click="handleOption(option)">
                            </OptionIcon>
                        </div>

                    </div>
                </template>


                <!-- asset view -->
                <template #secondary>
                    <div class="card-import__viewer">

                        <!-- model -->
                        <div v-if="isTypeModel" class="card-import__viewer-model">
                            <Viewer :asset="assets[0]" />
                            <Details :asset="assets[0]" />
                        </div>

                        <!-- images -->
                        <div v-else class="card-import__viewer-images">

                            <!-- TODO toggle to distinguish between sketch and image -->

                            <!-- list of imported assets -->
                            <div
                                class="card-import__viewer-images-assets"
                                :class="{
                                    'card-import__viewer-images-assets--compact': hasCompactAssetLayout
                                }">
                                <!-- imported assets -->
                                <OptionAsset 
                                    v-for="(asset, index) in assets" 
                                    :id="asset.id"
                                    :index="index" 
                                    :asset="asset" 
                                    :fileKey="fileKey"
                                    :compact="hasCompactAssetLayout"
                                    :selectable="false" 
                                    :hasPerspectives="hasMultipleAssets"
                                    @delete="handleDelete(asset)"
                                    @perspective="handlePerspective" />
                                <!-- add image option -->
                                <OptionIcon 
                                    v-if="!hasMaxAssets"
                                    :id="'add_image'"
                                    :index="0"
                                    :icon="'fa-solid fa-plus'"
                                    :label="'Add Image'" 
                                    :description="'(drag & drop)'"
                                    :compact="hasCompactAssetLayout"
                                    :strong="false"
                                    @click="handleOptionImage()">
                                </OptionIcon>
                            </div>

                            <AtomText 
                                v-if="hasSingleAsset && isTypeImage"                          
                                text="Import up to six images for maximum details." 
                                class="hide-on-mobile" />

                        </div>

                    </div>
                </template>

            </AtomSwap>

        </template>

    </CardBase>
</template>

<script>
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import CATEGORY from '@generio-ai/library-legacy/constants/category'
import LEVELS from '@generio-ai/library-legacy/constants/levels'
import MIMES from '@generio-ai/library-legacy/constants/mimes'
import QUALITY from '@generio-ai/library-legacy/constants/quality'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import Details from '@generio-ai/library-legacy/components/details'
import { Viewer } from '@generio-ai/library-legacy/components/viewer'
import ACTION from '@/constants/action.js'
import CARD from '@/constants/card.js'
import OPTION from '@/constants/option.js'
import AtomSwap from '@/components/common/atom/AtomSwap.vue'
import AtomText from '@/components/common/atom/AtomText.vue'
import CardBase from '@/components/flow/cards/CardBase.vue'
import OptionIcon from '@/components/flow/options/OptionIcon.vue'
import OptionAsset from '@/components/flow/options/OptionAsset.vue'
import { readFilesWithParameters } from '@/utils/file.js'

export default {
    name: 'CardImport',
    components: {
        Viewer,
        CardBase,
        OptionIcon,
        OptionAsset,
        AtomSwap,
        AtomText,
        Details
    },
    props: {
        app: {
            type: String,
            required: true
        },
        card: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            dragging: false,
            message: '',
        }
    },
    computed: {
		...mapGetters('flows', [
            'getPrimaryCardForCard'
        ]),

        isActionable() {
            return this.hasAssets
        },
        isHighlight() {
            return this.dragging
        },

        isTypeImage() {
            return this.card.category === CATEGORY.PHOTO && !this.promptDual && !this.isModeSketch
        },
        isTypeSketch() {
            return this.card.category === CATEGORY.PHOTO && !this.promptDual && this.isModeSketch
        },
        isTypeTexture() {
            return this.card.category === CATEGORY.PHOTO && this.promptDual
        },
        isTypeModel() {
            return this.card.category === CATEGORY.MODEL
        },

        isModeSketch() {
            return this.mode === 'black' || this.mode === 'grayscale'
        },

        hasAssets() {
            if (this.assets === null || this.assets === undefined) return false
            return this.assets.length > 0
        },
        hasSingleAsset() {
            if (this.assets === null || this.assets === undefined) return false
            return this.assets.length == 1
        },
        hasMultipleAssets() {
            if (this.assets === null || this.assets === undefined) return false
            return this.assets.length > 1
        },
        hasMaxAssets() {
            if (this.assets === null || this.assets === undefined) return false
            return this.assets.length >= this.maxAssets
        },
        hasCompactAssetLayout() {
            if (this.assets === null || this.assets === undefined) return false
            return this.assets.length > 3
        },

        hasCompletePerspectives() {
            if (!this.hasAssets) return false
            if (!this.hasMultipleAssets) return true
            return this.assetsWithoutPerspective.length === 0
        },
        hasMissingPerspectives() {
            if (this.isTypeModel) return false
            if (this.isTypeTexture) return false
            if (!this.hasAssets) return false
            if (!this.hasMultipleAssets) return false
            return this.assetsWithoutPerspective.length > 0
        },

        type() {
            if (this.isTypeModel) return ASSETS.TYPES.MODEL
            return ASSETS.TYPES.IMAGE
        },
        formats() {
            if (this.isTypeModel) {
                return Object.keys(MIMES.MODEL).map(ext => `.${ext}`).join(', ')
            }
            return Object.keys(MIMES.IMAGE).map(ext => `.${ext}`).join(', ')
        },
        subject() {
            if (this.isTypeModel) return 'model'
            if (this.isTypeTexture) return 'texture'
            if (this.isTypeSketch) return 'sketch'
            return 'images'
        },
        maxAssets() {
            if (this.isTypeModel) return 1
            if (this.isTypeSketch) return 1
            return 6
        },

        title() {
            return `Import your ${this.subject}.`
        },
        optionDropDescription() {
            return OPTION.IMPORT_DROP.description(this.subject)
        },
        options() {
            return [
                {
					index: 1,
                    active: true,
					...OPTION.IMPORT_UPLOAD,
                },
                {
                    index: 2,
                    active: true,
                    ...OPTION.IMPORT_LIBRARY,
                },
                {
					index: 3,
					active: this.isTypeImage,
					...OPTION.IMPORT_PHOTO,
                }
            ]
        },
        activeOptions() {
            return this.options.filter(option => option.active)
        },
        actions() {
            if (this.hasAssets) {
                if (this.isTypeModel) {
                    return [
                        ACTION.GENERATE_TEXTURE,
                        ACTION.MAKE_WATERTIGHT,
                        ACTION.REFINE_MODEL
                    ]
                }
                if (this.isTypeTexture) {
                    return [
                        ACTION.GENERATE_TEXTURE
                    ]
                }
                if (this.isTypeSketch) {
                    return [
                        ACTION.EDIT_SKETCH,
                        ACTION.DESCRIBE_SKETCH
                    ]
                }
                if (this.assets?.length == 1) {
                    return [
                        ACTION.GENERATE_MODEL,
                        ACTION.HIGHLIGHT_EDIT,
                        ACTION.DESCRIBE_EDIT,
                        ACTION.MAKE_SKETCH
                    ]
                }
                return [
                    ACTION.GENERATE_MODEL
                ]   
            }
            return []
        },
        guidance() {
            if (this.hasMissingPerspectives) {
                return 'Please select a perspective for each image to continue.'
            }
            return null
        },

        index() {
            return this.card.index
        },
        category() {
            return this.card.category
        },
        prompt() {
            return this.card.prompt
        },
        promptQuality() {
            return this.prompt?.quality || QUALITY.DEFAULT
        },
        promptSolid() {
            return this.prompt?.solid || true
        },
        promptDual() {
            return this.prompt?.dual || false
        },

        assets() {
            return this.card.assets
        },
        assetsWithoutPerspective() {
            return (this.assets || []).filter(asset => !asset?.additional?.perspective)
        },
        fileKey() {
            return ASSETS.FILES.KEY.DEFAULT
        },

        primaryCard() {
			return this.getPrimaryCardForCard(this.app, this.card, false)
		},
        primaryAsset() {
            return this.assets?.[0] || null
        },
        primaryFile() {
            return this.primaryAsset?.files?.[this.fileKey] || null
        },
        primaryDetails() {
            return this.primaryFile?.details || null
        },

        mode() {
            return this.primaryDetails?.mode || null
        }
    },	
    methods: {
        ...mapActions('analytics', [
            'logError'
        ]),
        ...mapActions('assets', [
            'updateAdditional'
        ]),
        ...mapActions('dialogs', {
			openDialog: 'open'
        }),
        ...mapActions('notifications', [
            'show'
        ]),
        ...mapActions('flows', [
            'newTransaction',
            'commitUpdateAssets',
            'fetchBase',
            'fetchMakeSketch',
            'intermediatePostAssets',
            'intermediateCloneAssets'
        ]),
        ...mapActions('windows', {
            openWindow: 'open'
        }),

        handleOption(option) 
        {
            // update card
            this.card.option = option.id

            switch(option.id) 
            {
				case OPTION.IMPORT_UPLOAD.id:
					const fileInput = this.$refs.fileInput
					if (fileInput) {
						fileInput.click()
					}
					break
                case OPTION.IMPORT_LIBRARY.id:
                    this.openWindow({
                        component: 'library',
                        payload: {
                            type: this.isTypeModel ? 
                                ASSETS.TYPES.MODEL : ASSETS.TYPES.IMAGE,
                            maxAssets: this.maxAssets,
                            callback: this.handleLibrary
                        }
                    })
                    break
				case OPTION.IMPORT_PHOTO.id:
					this.openDialog({
						component: 'camera',
						payload: {
							callback: this.handleImport
						}
					})
					break
			}
        },
        handleOptionImage() {
            this.handleOption(OPTION.IMPORT_UPLOAD)
        },
        handleAction(action) {
            switch(action.id) 
            {
                case ACTION.DESCRIBE_EDIT.id:
					this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.PROMPT,
							category: CATEGORY.PHOTO,
						})
					})
					break  

                case ACTION.DESCRIBE_SKETCH.id:
					this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.PROMPT,
							category: CATEGORY.SKETCH,
						})
					})
					break  

                case ACTION.EDIT_SKETCH.id:
                    this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.IMAGE,
							category: CATEGORY.SKETCH,
						})
					})
					break

                case ACTION.MAKE_SKETCH.id:
                    this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchMakeSketch({
							component: CARD.IMAGE
						})
					})
					break

                case ACTION.GENERATE_MODEL.id:
					this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.GENERATE_GEOMETRY,
							category: CATEGORY.PHOTO,
						})
					})
					break

                case ACTION.GENERATE_TEXTURE.id:
					this.newTransaction({
						app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
                        taskFetch: this.fetchBase({
							component: this.isTypeModel ? CARD.SELECT_TEXTURE : CARD.GENERATE_TEXTURE,
							category: CATEGORY.MODEL,
						})
					})
					break

                case ACTION.HIGHLIGHT_EDIT.id:
                    this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.IMAGE,
							category: CATEGORY.PHOTO,
						})
					})
					break

                case ACTION.MAKE_WATERTIGHT.id:
                    this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.WATERTIGHT,
							category: CATEGORY.MODEL,
						})
					})
                    break

                case ACTION.REFINE_MODEL.id:
                    this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.OPTIMIZE,
							category: CATEGORY.MODEL,
						})
					})
                    break
            }
        },

        handleDragOver(event) {
            event.preventDefault()
            if (this.hasMaxAssets) return
            this.dragging = true
        },
        handleDragLeave(event) {
            event.preventDefault()
            this.dragging = false
        },
        async handleDrop(event) {
            event.preventDefault()
			this.dragging = false
            if (this.hasMaxAssets) {
                this.showMaxMessage()
                return
            }
			const files = event.dataTransfer.files
			if (!files?.length) return
			const data = await this.importFiles(files)
			this.handleImport(data)
        },

        async handleSelect(event) {
			const files = event.target.files
			if (!files?.length) return
            const data = await this.importFiles(files)
            this.handleImport(data)
            // try clearing the select window
            try {
                if (event?.target) {
                    event.target.value = null
                }
            } catch (e) {}
		},

        async handleImport(payload) {
            const {
                fileData,
                fileParameters
            } = this.normalizeImportPayload(payload)

            // validate data
            if (!fileData || fileData.length === 0) return

            // allow only up to three images
            if (this.hasMaxAssets) {
                this.showMaxMessage()
                return
            }

            // enforce limit for importing data
            let limitedFileData = fileData
            let limitedFileParameters = fileParameters
            const remainingSlots = this.maxAssets - (this.assets?.length || 0)
            if (limitedFileData.length > remainingSlots) {
                limitedFileData = limitedFileData.slice(0, remainingSlots)
                limitedFileParameters = limitedFileParameters.slice(0, remainingSlots)
                this.showMaxMessage()
            }

            // inform user
            this.loading = true
            this.message = `importing ${this.subject}`

            // perform import of model/images
            this.intermediatePostAssets({
                app: this.app,
                card: this.card, 
                parentCard: this.primaryCard,
                fileData: limitedFileData,
                fileParameters: limitedFileParameters,
                ignore: false,
                preload: true
            })
            .then(response => {
                const assets = response.assets || []
                this.card.assets = this.card.assets || []
                if (assets.length > 0) {
                    this.card.assets = [...this.card.assets, ...assets]
                }
            })
            .catch(error => {
                this.logError({
                    name: `Import Failed`,
                    description: error?.message || 'no details available',
                    additional: error
                })
                this.show({
                    message: `Import of ${this.subject} has failed`,
                    level: LEVELS.ERROR
                })
            })
            .finally(() => {
                this.loading = false
            })
        },
        handleLibrary(assets) {
            // validate assets
            if (!assets || assets.length === 0) return

            // allow only up to three images
            if (this.hasMaxAssets) {
                this.showMaxMessage()
                return
            }

            // enforce limit for importing data
            if (assets.length + (this.assets?.length || 0) > this.maxAssets) {
                assets = assets.slice(0, this.maxAssets - (this.assets?.length || 0))
                this.showMaxMessage()
            }

            // inform user
            this.loading = true
            this.message = `importing ${this.subject}`

            // perform clone of model/images
            this.intermediateCloneAssets({
                app: this.app,
                card: this.card, 
                parentCard: this.primaryCard,
                assets: assets,
                ignore: false,
                preload: true
            })
            .then(response => {
                const assets = response.assets || []
                this.card.assets = this.card.assets || []
                if (assets.length > 0) {
                    this.card.assets = [...this.card.assets, ...assets]
                }
            })
            .catch(error => {
                this.logError({
                    name: `Import Failed`,
                    description: error?.message || 'no details available',
                    additional: error
                })
                this.show({
                    message: `Import of ${this.subject} has failed`,
                    level: LEVELS.ERROR
                })
            })
            .finally(() => {
                this.loading = false
            })
        },
        handleDelete(asset) {
            // TODO should be a simpler delete handler than the context menu
			this.card.assets = this.card.assets.filter(a => a.id !== asset.id)
            if (!this.hasAssets) this.card.option = null
        },
        async handlePerspective({ asset, perspective }) {
            if (!asset || !asset.id) return
            // set perspective on asset
            asset.additional.perspective = perspective || null
            // commit change to server
            await this.updateAdditional({
                asset: asset,
                additional: asset.additional
            })
        },

        showMaxMessage() {
            if (this.isTypeModel) {
                this.show({
                    message: 'You have reached the maximum of one model.',
                    level: LEVELS.WARNING
                })
            }
            else {
                this.show({
                    message: 'You have reached the maximum of six images.',
                    level: LEVELS.WARNING
                })
            }
        },

        normalizeImportPayload(payload) {
            if (
                payload &&
                typeof payload === 'object' &&
                !Array.isArray(payload) &&
                (payload.fileData !== undefined || payload.fileParameters !== undefined)
            ) {
                const fileData = Array.isArray(payload.fileData) ?
                    payload.fileData :
                    payload.fileData ? [payload.fileData] : []
                const fileParameters = Array.isArray(payload.fileParameters) ?
                    payload.fileParameters :
                    fileData.map((_, index) => index === 0 ? payload.fileParameters || null : null)

                return { fileData, fileParameters }
            }

            const fileData = Array.isArray(payload) ? payload : payload ? [payload] : []
            return {
                fileData,
                fileParameters: fileData.map(() => null)
            }
        },

        async importFiles(files) {
            try {
                return await readFilesWithParameters(files, this.type)
            }
            catch (error) {
                this.logError({
                    name: `Import Failed`,
                    description: error?.message || 'no details available',
                    additional: error
                })
                this.show({
                    message: error?.message || `Import of ${this.subject} has failed`,
                    level: LEVELS.ERROR
                })
            }
        }
    }
}
</script>

<style scoped>
.card-import {
    transition: filter 0.3s;
}

.card-import.highlight {
    filter: invert(0.1);
}

.card-import__import,
.card-import__viewer-images {
    width: calc(100% - 60px);
    flex: 1 1 auto;
    height: auto;
    min-height: 0;
    margin: 30px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 30px;
}

.card-import__import-field {
    width: 100%;
    flex-grow: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
	font-size: 2rem;
    border: 5px dashed rgb(var(--v-theme-darkerbase));
    background-color: rgb(var(--v-theme-darkbase));
	transition: background-color 0.3s, border-color 0.3s;
}

.card-import__import-field.highlight {
	background-color: rgba(var(--v-theme-color1), 0.2);
}

/* IMPORTANT: adjust to screen similar to assets */
.card-import__import-options {
    flex-shrink: 1;
    width: 100%;
    display: flex;
    flex-wrap: wrap; 
    flex-direction: row;
    align-items: flex-end;
    align-content: flex-end;
    justify-content: center;
    gap: 50px;
}

.card-import__viewer {
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.card-import__viewer-model {
    width: 100%;
    height: 100%;
}

.card-import__viewer-images-assets {
    flex-shrink: 1;
    width: 100%;
    display: flex;
    flex-wrap: wrap; 
    flex-direction: row;
    align-items: flex-end;
    align-content: flex-end;
    justify-content: center;
    gap: 50px;
    padding-top: 50px;
    padding-bottom: 50px;
}

.card-import__viewer-images-assets:not(.card-import__viewer-images-assets--compact) {
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
}

.card-import__viewer-images-assets.card-import__viewer-images-assets--compact {
    display: grid;
    grid-template-columns: repeat(3, max-content);
    grid-auto-rows: max-content;
    justify-content: center;
    align-items: end;
}

@media (max-width: 2000px) {
    .card-import__import-options {
        gap: 40px;
    }

    .card-import__viewer-images-assets {
        gap: 40px;
        padding-top: 40px;
        padding-bottom: 40px;
    }
}

@media (max-width: 1600px) {
    .card-import__import-options {
        gap: 25px;
    }

    .card-import__viewer-images-assets {
        gap: 25px;
        padding-top: 25px;
        padding-bottom: 25px;
    }
}

@media (max-width: 1400px) {
    .card-import__import-options {
        gap: 20px;
    }

    .card-import__viewer-images-assets {
        gap: 20px;
        padding-top: 20px;
        padding-bottom: 20px;
    }
}

@media (max-width: 1200px) {
    .card-import__import-options {
        gap: 15px;
    }

    .card-import__viewer-images-assets {
        gap: 15px;
        padding-top: 15px;
        padding-bottom: 15px;
    }
}

@media (max-height: 1000px) {
    .card-import__import,
    .card-import__viewer-images {
        width: calc(100% - 40px);
        margin: 20px;
        gap: 20px;
    }
}

@media (max-width: 800px) {
    .card-import__import-options {

    }

    .card-import__viewer-images-assets {
        flex-shrink: unset;
        flex-grow: 1;
        align-items: center;
        align-content: center;
        padding-top: 10px;
        padding-bottom: 10px;
    }
}

@media (max-width: 600px) {
    .card-import__import-field {
        display: none;
    }

    .card-import__import-options {
        gap: 10px;
    }

    .card-import__viewer-images-assets {
        gap: 10px;
    }
}

	@media (max-width: 400px) {
	    .card-import__import,
	    .card-import__viewer-images {
	        width: calc(100% - 20px);
        margin: 10px;
        gap: 10px;
    }

    .card-import__import-options {
        gap: 5px;
    }

	    .card-import__viewer-images-assets {
	        gap: 5px;
	    }
	}

    .card-import__viewer-images-assets.card-import__viewer-images-assets--compact {
        gap: 20px;
        padding-top: 20px;
        padding-bottom: 20px;
    }

    @media (max-width: 1600px) {
        .card-import__viewer-images-assets.card-import__viewer-images-assets--compact {
            gap: 15px;
            padding-top: 15px;
            padding-bottom: 15px;
        }
    }

    @media (max-width: 1200px) {
        .card-import__viewer-images-assets.card-import__viewer-images-assets--compact {
            gap: 12px;
            padding-top: 12px;
            padding-bottom: 12px;
        }
    }

    @media (max-width: 600px) {
        .card-import__viewer-images-assets.card-import__viewer-images-assets--compact {
            gap: 8px;
            padding-top: 8px;
            padding-bottom: 8px;
        }
    }
</style>
